import { getMessages } from '../chatv1/transport'
import { parseFileCard } from '../transport/api'

/**
 * FINDING A SHARED FILE THAT IS OLDER THAN THE PAGE ON SCREEN.
 *
 * A file notice names a path built for humans — `~/project-files/<id>/<name>` — from
 * which the project id is recoverable and the FILE id is not. `/project/file/blob` needs
 * both, so the id has to be read back out of the message that shared it, and there is no
 * endpoint that looks a project file up by name.
 *
 * ProjectImage did that read against the project's newest 50 messages, which is what
 * `getProjectHistory` returns by default. Share a photo, talk for an afternoon, and the
 * message carrying it falls off the back of that page — from then on the inline preview
 * read "not found in this project" for a file that was still perfectly present. The
 * statement was not merely unhelpful, it was false, and it got MORE false the longer a
 * project stayed alive: the only projects that showed their files correctly were the
 * quiet ones.
 *
 * So: page backward until it is found or the history runs out. The walk starts from the
 * oldest message the caller has already searched, so the common case — a file shared
 * recently, in the page the panel already has cached — costs NOTHING. Only a miss pays,
 * which is the right way round.
 *
 * MOST RECENT MATCH WINS is preserved and is not incidental: `_agent_blob_link` repoints
 * a re-shared filename at the newer file, so the newest match is the one the sharer was
 * looking at. Pages are walked newest-first and each page is sorted by seq descending
 * before scanning, because the history endpoint does not promise an order.
 */

/** A page here is bigger than the panel's 50: this walk is the uncommon path, and each
 *  round trip costs more than the rows it carries. */
export const FILE_SCAN_PAGE = 100

/**
 * The walk is BOUNDED, and the bound is visible in the answer.
 *
 * A project with tens of thousands of messages would otherwise turn one missing preview
 * into an unbounded read. Ten pages is deep enough that a real share is found and shallow
 * enough that a genuinely absent file is answered quickly — and when the bound is what
 * stopped the walk, `exhausted` is false and the caller says "not in the last N" rather
 * than repeating the lie this whole module exists to remove.
 */
export const FILE_SCAN_MAX_PAGES = 10

export type FileScan = {
  fileId: string | null
  /** True when the history genuinely ran out — the only case where "not in this project"
   *  is an honest thing to say. False means the bound above stopped the walk. */
  exhausted: boolean
  /** How many messages were actually looked at, so the message on screen can be specific. */
  scanned: number
}

const idIn = (body: string | null | undefined, name: string): string | null =>
  parseFileCard(body)?.attachments?.find((a) => a.name === name)?.file_id ?? null

export async function findProjectFileId(
  projectId: string,
  name: string,
  /** The oldest seq already searched by the caller; the walk starts strictly before it. */
  beforeSeq: number,
): Promise<FileScan> {
  let cursor: number | undefined = beforeSeq
  let scanned = 0

  for (let page = 0; page < FILE_SCAN_MAX_PAGES; page++) {
    const { messages } = await getMessages(projectId, cursor, FILE_SCAN_PAGE)
    if (!messages.length) return { fileId: null, exhausted: true, scanned }

    const newestFirst = [...messages].sort((a, b) => b.seq - a.seq)
    for (const m of newestFirst) {
      scanned += 1
      const hit = idIn(m.envelope?.body, name)
      if (hit) return { fileId: hit, exhausted: false, scanned }
    }

    // A short page is the end of the history; a full one may have more behind it.
    if (messages.length < FILE_SCAN_PAGE) return { fileId: null, exhausted: true, scanned }
    cursor = newestFirst[newestFirst.length - 1]!.seq
  }
  return { fileId: null, exhausted: false, scanned }
}
